import { useEffect, useState } from "react";
import { BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, C, fmtChips } from "../../lib/api";

const PAGE = 25;

// Separate window for the full ledger: 25 entries per fetch, "Show more" appends the next 25.
export const LedgerDialog = ({ open, onOpenChange }) => {
  const [entries, setEntries] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (skip) => {
    setLoading(true);
    try {
      const r = await api.get(C.ApiRoutes.WALLET_LEDGER, { params: { limit: PAGE, skip } });
      setEntries((prev) => (skip === 0 ? r.data : [...prev, ...r.data]));
      if (r.data.length < PAGE) setDone(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setEntries([]); setDone(false); load(0); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gold-card text-slate-100 max-w-xl max-h-[80vh] flex flex-col" data-testid="ledger-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-amber-50"><BookOpen className="w-4 h-4 gold-text" /> Ledger <span className="text-xs text-slate-400 font-sans font-normal ml-auto">append-only · UTC</span></DialogTitle>
        </DialogHeader>
        <div className="flex flex-col divide-y divide-white/5 overflow-y-auto pane-scroll pr-1" data-testid="ledger-list">
          {entries.map((e) => (
            <div key={e.entry_id} data-testid={`ledger-entry-${e.type}`} className="flex items-center justify-between py-2 text-sm">
              <div>
                <div className="text-slate-200">{e.type.replace(/_/g, " ").toLowerCase()}{e.meta?.package_id ? ` · ${e.meta.package_id}` : ""}</div>
                <div className="text-[10px] font-mono text-slate-500">{new Date(e.created_at).toLocaleString()} · {e.currency}</div>
              </div>
              <span className={`font-mono ${e.amount >= 0 ? "text-emerald-300" : "text-red-300"}`}>{e.amount >= 0 ? "+" : "−"}{e.currency === "CHIPS" ? fmtChips(Math.abs(e.amount)) : Math.abs(e.amount).toLocaleString()}{e.currency === "VIP" ? " VIP" : ""}</span>
            </div>
          ))}
          {!loading && entries.length === 0 && <div className="text-sm text-slate-400 py-2">No entries yet.</div>}
        </div>
        <div className="flex items-center justify-between pt-2 text-xs text-slate-400">
          <span data-testid="ledger-count">{entries.length} shown</span>
          {!done && <button data-testid="ledger-show-more" disabled={loading} onClick={() => load(entries.length)} className="btn-gold rounded-full px-4 py-1.5 font-display disabled:opacity-50">{loading ? "Loading…" : `Show ${PAGE} more`}</button>}
          {done && entries.length > 0 && <span data-testid="ledger-end">End of ledger</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
